import { useEffect, useMemo, useRef, useState, useTransition } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../../auth/AuthContext.jsx';
import { useClientData } from '../ClientLayout.jsx';
import { api, demoStore } from '../../lib/api.js';
import { reportAppNotification } from '../../lib/appNotifications.js';
import {
  appendLocalMessage,
  applyRemoteMessages,
  deleteLocalMessage,
  getLocalMessages,
  listDemandConversations,
  seedDemoMessages,
} from '../../lib/communicationsStore.js';
import {
  isMatterDetailPreviewId,
  seedMatterDetailPreview,
} from '../../lib/matterDetailPreviewSeed.js';
import { statusDisplayLabel } from '../../lib/matterPipeline.js';
import { debtorCompanyPrimaryName } from '../../lib/debtorIdentity.js';
import { matterDemandId } from '../../lib/demandId.js';
import {
  buildLocalMessage,
  filterMessageAttachmentFiles,
  mergeMessageAttachmentNames,
  messageAttachmentUrl,
  messageHasContent,
} from '../../lib/messageAttachments.js';
import {
  MESSAGES_FILTER_OPTIONS,
  MESSAGES_SORT_OPTIONS,
  filterMessageConversations,
  isConversationUnread,
  loadInboxSeenMap,
  loadMessagesFilter,
  loadMessagesSort,
  markConversationSeen,
  matterAccountNumber,
  matterInvoiceNumber,
  saveMessagesFilter,
  saveMessagesSort,
  sortMessageConversations,
} from '../../lib/messagesInbox.js';
import { messageComposerPlaceholder } from '../../lib/messageComposerExtras.js';
import { getLocalSettings } from '../../lib/settingsStore.js';
import {
  canUploadEvidence,
  estimateFileBytes,
  evidenceVaultLimitMessage,
} from '../../lib/evidenceVault.js';
import {
  deleteUploadBlobs,
  putUploadBlobs,
} from '../../lib/uploadBlobStore.js';
import ListPagination, { useListPagination } from '../ListPagination.jsx';
import MessageComposer, { MessageBubble } from '../MessageComposer.jsx';
import { scrollMessageThreadToLatest } from '../../lib/messageThreadScroll.js';

function formatWhen(value, { compact = false } = {}) {
  if (!value) return '';
  try {
    const date = new Date(value);
    if (compact) {
      return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
    }
    return date.toLocaleString();
  } catch {
    return String(value);
  }
}

function statusLabel(status) {
  return statusDisplayLabel(status);
}

function attorneyLabel(matter) {
  return matter?.Attorney_Name || 'Attorney (not assigned yet)';
}

function conversationTitle(matter, matterId) {
  return debtorCompanyPrimaryName(matter) || `Demand ${matterId}`;
}

function FilterIcon() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
      <path
        d="M4 6h16M7 12h10M10 18h4"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.9"
        strokeLinecap="round"
      />
    </svg>
  );
}

function SortIcon() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
      <path
        d="M8 5v14M8 5l-3 3M8 5l3 3M16 19V5M16 19l-3-3M16 19l3-3"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.9"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function useMediaQuery(query) {
  const [matches, setMatches] = useState(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return false;
    }
    return window.matchMedia(query).matches;
  });

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return undefined;
    }
    const media = window.matchMedia(query);
    const onChange = () => setMatches(media.matches);
    onChange();
    if (typeof media.addEventListener === 'function') {
      media.addEventListener('change', onChange);
      return () => media.removeEventListener('change', onChange);
    }
    media.addListener(onChange);
    return () => media.removeListener(onChange);
  }, [query]);

  return matches;
}

export default function MessagesPage() {
  const { user } = useAuth();
  const { matters, openMatter, vaultUsage } = useClientData();
  const [searchParams, setSearchParams] = useSearchParams();
  const [q, setQ] = useState(() => searchParams.get('q') || '');
  const [sortBy, setSortBy] = useState(() => loadMessagesSort());
  const [filterBy, setFilterBy] = useState(() => loadMessagesFilter());
  const [seenMap, setSeenMap] = useState(() => loadInboxSeenMap());
  const [draft, setDraft] = useState('');
  const [attachmentFiles, setAttachmentFiles] = useState([]);
  const [error, setError] = useState('');
  const [refreshKey, setRefreshKey] = useState(0);
  const [remoteMessages, setRemoteMessages] = useState([]);
  const [pending, startTransition] = useTransition();
  const [deletingMessageId, setDeletingMessageId] = useState('');
  const [mobileMenu, setMobileMenu] = useState('');
  const threadRef = useRef(null);
  const controlsRef = useRef(null);
  const isMobileLayout = useMediaQuery('(max-width: 900px)');

  const selectedId = searchParams.get('demand') || searchParams.get('matter') || '';
  const viewer = useMemo(
    () => ({
      email: user?.email || '',
      displayName: user?.displayName || user?.name || '',
      name: user?.name || user?.displayName || '',
    }),
    [user?.email, user?.displayName, user?.name]
  );

  useEffect(() => {
    setQ(searchParams.get('q') || '');
  }, [searchParams]);

  useEffect(() => {
    let seeded = false;
    for (const matter of matters) {
      if (isMatterDetailPreviewId(matter.ROWID)) {
        seedMatterDetailPreview(matter.ROWID, matter);
        seeded = true;
        continue;
      }
      const demoMessages = demoStore.messages?.[matter.ROWID];
      if (demoMessages?.length) {
        seedDemoMessages(matter.ROWID, demoMessages);
        seeded = true;
      }
    }
    if (seeded) setRefreshKey((n) => n + 1);
  }, [matters]);

  useEffect(() => {
    let cancelled = false;
    api
      .get('/server/client_api/messages')
      .then((data) => {
        if (!cancelled) setRemoteMessages(Array.isArray(data?.messages) ? data.messages : []);
      })
      .catch(() => {
        if (!cancelled) setRemoteMessages([]);
      });
    return () => {
      cancelled = true;
    };
  }, [refreshKey]);

  const conversations = useMemo(
    () => applyRemoteMessages(listDemandConversations(matters), remoteMessages),
    // refreshKey re-reads local threads after seeding / sending
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [matters, refreshKey, remoteMessages]
  );

  const mattersById = useMemo(() => {
    const map = new Map();
    for (const matter of matters || []) {
      if (matter?.ROWID) map.set(String(matter.ROWID), matter);
    }
    return map;
  }, [matters]);

  const hydratedConversations = useMemo(
    () =>
      conversations.map((thread) => {
        if (thread.matter) return thread;
        const matter = mattersById.get(String(thread.matterId));
        return matter ? { ...thread, matter } : thread;
      }),
    [conversations, mattersById]
  );

  const filtered = useMemo(() => {
    const query = q.trim().toLowerCase();
    const searched = !query
      ? hydratedConversations
      : hydratedConversations.filter((thread) => {
          const matter = thread.matter;
          const debtor = String(matter?.Debtor_Name || '').toLowerCase();
          const company = String(matter?.Debtor_Company || '').toLowerCase();
          const attorney = String(matter?.Attorney_Name || '').toLowerCase();
          const demandId = String(matter?.Demand_ID || '').toLowerCase();
          const account = matterAccountNumber(matter).toLowerCase();
          const invoice = matterInvoiceNumber(matter).toLowerCase();
          const preview = String(thread.preview || '').toLowerCase();
          return (
            debtor.includes(query) ||
            company.includes(query) ||
            attorney.includes(query) ||
            demandId.includes(query) ||
            account.includes(query) ||
            invoice.includes(query) ||
            preview.includes(query) ||
            String(thread.matterId).toLowerCase().includes(query)
          );
        });
    const matched = filterMessageConversations(searched, filterBy, {
      viewer,
      seenMap,
    });
    return sortMessageConversations(matched, sortBy, { viewer, seenMap });
  }, [hydratedConversations, q, filterBy, sortBy, viewer, seenMap]);

  const pagination = useListPagination(filtered, { storageKey: 'messages' });
  const {
    pageItems: pageThreads,
    page: listPage,
    pageSize: listPageSize,
    setPage: setListPage,
  } = pagination;

  const selectedThread =
    filtered.find((thread) => String(thread.matterId) === String(selectedId)) ||
    hydratedConversations.find((thread) => String(thread.matterId) === String(selectedId)) ||
    null;

  // Desktop keeps a split inbox/thread and auto-opens the first conversation.
  // Mobile is inbox-first: only open a thread from tap or a deep link.
  const active = selectedThread || (!isMobileLayout ? filtered[0] || null : null);
  const mobileThreadOpen = isMobileLayout && Boolean(selectedId && active);

  // Deep links / selection should land on the inbox page that contains the demand.
  useEffect(() => {
    if (!selectedId || !filtered.length) return;
    const index = filtered.findIndex(
      (thread) => String(thread.matterId) === String(selectedId)
    );
    if (index < 0) return;
    const neededPage = Math.floor(index / listPageSize) + 1;
    if (neededPage !== listPage) {
      setListPage(neededPage);
    }
  }, [selectedId, filtered, listPage, listPageSize, setListPage]);

  useEffect(() => {
    if (!active) return;
    if (String(selectedId) === String(active.matterId)) return;
    // Wait until the demand row exists so we do not flash an orphan/null matter.
    if (!active.matter && mattersById.has(String(active.matterId))) return;
    // On mobile, stay on the inbox until the user picks a demand (or arrives via deep link).
    if (isMobileLayout) return;
    setSearchParams({ demand: String(active.matterId) }, { replace: true });
  }, [active, selectedId, setSearchParams, mattersById, isMobileLayout]);

  useEffect(() => {
    setDraft('');
    setAttachmentFiles([]);
    setError('');
  }, [active?.matterId]);

  useEffect(() => {
    scrollMessageThreadToLatest(threadRef.current);
  }, [active?.matterId, active?.messageCount, refreshKey]);

  // Opening a conversation marks foreign activity as read for inbox sorting.
  useEffect(() => {
    if (!active?.matterId) return;
    setSeenMap((prev) => markConversationSeen(active, viewer, prev));
  }, [active?.matterId, active?.messageCount, refreshKey, viewer]);

  function selectConversation(matterId) {
    setSearchParams({ demand: String(matterId) });
  }

  function backToInbox() {
    setSearchParams({}, { replace: true });
  }

  function onSortChange(nextSort) {
    const saved = saveMessagesSort(nextSort);
    setSortBy(saved);
    setListPage(1);
    setMobileMenu('');
  }

  function onFilterChange(nextFilter) {
    const saved = saveMessagesFilter(nextFilter);
    setFilterBy(saved);
    setListPage(1);
    setMobileMenu('');
  }

  const filterGroups = useMemo(() => {
    const top = [];
    const status = [];
    for (const option of MESSAGES_FILTER_OPTIONS) {
      if (option.group === 'Status') status.push(option);
      else top.push(option);
    }
    return { top, status };
  }, []);

  const activeFilterLabel =
    MESSAGES_FILTER_OPTIONS.find((option) => option.id === filterBy)?.label || 'All';
  const activeSortLabel =
    MESSAGES_SORT_OPTIONS.find((option) => option.id === sortBy)?.label || 'Last activity';
  const filterActive = filterBy !== 'all';
  const sortActive = sortBy !== 'activity';

  useEffect(() => {
    if (!mobileMenu) return undefined;
    function onPointerDown(event) {
      if (controlsRef.current?.contains(event.target)) return;
      setMobileMenu('');
    }
    function onKeyDown(event) {
      if (event.key === 'Escape') setMobileMenu('');
    }
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [mobileMenu]);

  useEffect(() => {
    if (!isMobileLayout) setMobileMenu('');
  }, [isMobileLayout]);

  function onAttachmentFilesChange(filesOrNames, { mode } = {}) {
    if (mode === 'remove') {
      const remove = new Set((filesOrNames || []).map((n) => String(n).toLowerCase()));
      setAttachmentFiles((prev) =>
        prev.filter((file) => !remove.has(String(file?.name || file || '').toLowerCase()))
      );
      return;
    }
    const billing = getLocalSettings(user?.email || '').billing || {};
    const accepted = filterMessageAttachmentFiles(filesOrNames);
    const addBytes = accepted.reduce(
      (sum, file) => sum + estimateFileBytes(file?.name || file, file?.size),
      0
    );
    const gate = canUploadEvidence({
      usedBytes: vaultUsage?.usedBytes || 0,
      addBytes,
      billing,
    });
    if (!gate.ok) {
      setError(evidenceVaultLimitMessage(gate.usage));
      return;
    }
    setError('');
    setAttachmentFiles((prev) => {
      const names = mergeMessageAttachmentNames(
        prev.map((file) => file.name),
        accepted
      );
      const byName = new Map(
        [...prev, ...accepted].map((file) => [String(file.name).toLowerCase(), file])
      );
      return names.map((name) => byName.get(String(name).toLowerCase())).filter(Boolean);
    });
  }

  function sendMessage(e) {
    e.preventDefault();
    if (!active?.matter) return;
    const body = draft.trim();
    const files = [...attachmentFiles];
    const names = files.map((file) => String(file.name || '').trim()).filter(Boolean);
    if (!messageHasContent(body, names)) return;
    setError('');

    startTransition(async () => {
      const attachmentUrl = messageAttachmentUrl(names);

      async function rememberAttachments(message) {
        if (!files.length || !message?.ROWID) return;
        try {
          await putUploadBlobs('message', message.ROWID, files);
        } catch {
          // Local preview blobs are best-effort.
        }
      }

      try {
        const data = await api.post(
          `/server/client_api/matters/${encodeURIComponent(active.matterId)}/messages`,
          { body, attachmentUrl }
        );
        await rememberAttachments(data.message);
        appendLocalMessage(active.matterId, data.message);
        setDraft('');
        setAttachmentFiles([]);
        setRefreshKey((n) => n + 1);
      } catch {
        const message = buildLocalMessage({
          matterId: active.matterId,
          senderName: user?.displayName || 'You',
          body,
          attachmentNames: names,
        });
        await rememberAttachments(message);
        appendLocalMessage(active.matterId, message);
        getLocalMessages(active.matterId);
        void reportAppNotification({
          kind: 'newMessage',
          matterId: active.matterId,
          messageId: message.ROWID,
          snippet: body,
        });
        setDraft('');
        setAttachmentFiles([]);
        setRefreshKey((n) => n + 1);
      }
    });
  }

  function deleteMessage(message) {
    if (!active?.matterId || !message?.ROWID || deletingMessageId) return;
    setError('');
    setDeletingMessageId(message.ROWID);
    const matterId = active.matterId;
    const messageId = message.ROWID;
    startTransition(async () => {
      try {
        await api.del(
          `/server/client_api/matters/${encodeURIComponent(matterId)}/messages/${encodeURIComponent(messageId)}`
        );
      } catch (err) {
        if (err?.status === 403) {
          setDeletingMessageId('');
          setError(err.message || 'Could not delete that message.');
          return;
        }
      }
      const removed = deleteLocalMessage(matterId, messageId);
      setDeletingMessageId('');
      if (!removed && !remoteMessages.some((item) => String(item?.ROWID) === String(messageId))) {
        setError('Could not delete that message.');
        return;
      }
      setRemoteMessages((prev) => prev.filter((item) => String(item?.ROWID) !== String(messageId)));
      void deleteUploadBlobs('message', messageId);
      setRefreshKey((n) => n + 1);
    });
  }

  const activeDemandId = matterDemandId(active?.matter);
  const activeAccount = matterAccountNumber(active?.matter);
  const activeInvoice = matterInvoiceNumber(active?.matter);
  const pageClassName = [
    'client-page',
    'messages-page',
    mobileThreadOpen ? 'is-mobile-thread' : '',
    isMobileLayout && !mobileThreadOpen ? 'is-mobile-inbox' : '',
  ]
    .filter(Boolean)
    .join(' ');
  const workspaceClassName = [
    'messages-workspace',
    mobileThreadOpen ? 'is-mobile-thread' : '',
    isMobileLayout && !mobileThreadOpen ? 'is-mobile-inbox' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={pageClassName}>
      {!mobileThreadOpen ? (
        <div className="section-head">
          <div>
            <h1>Messages</h1>
            <p>
              Message your attorney about active demands. Each conversation is tied to one
              demand record.
            </p>
          </div>
        </div>
      ) : null}

      {!conversations.length ? (
        <div className="messages-empty">
          <p className="settings-sublead">
            Use the + button in the header to create a demand, then message your attorney
            from that record.
          </p>
        </div>
      ) : (
        <>
          {!mobileThreadOpen ? (
            <div
              className={`messages-inbox-controls${
                isMobileLayout ? ' is-mobile-compact' : ''
              }`}
              ref={controlsRef}
            >
              {isMobileLayout ? (
                <>
                  <div className="messages-search-bar">
                    <input
                      type="search"
                      value={q}
                      onChange={(e) => setQ(e.target.value)}
                      placeholder="Search messages…"
                      aria-label="Find a demand conversation"
                    />
                    <button
                      type="button"
                      className={`messages-refine-btn${
                        filterActive || mobileMenu === 'filter' ? ' is-active' : ''
                      }`}
                      aria-label={`Filter conversations${
                        filterActive ? `: ${activeFilterLabel}` : ''
                      }`}
                      title={filterActive ? `Filter: ${activeFilterLabel}` : 'Filter'}
                      aria-expanded={mobileMenu === 'filter'}
                      aria-haspopup="menu"
                      onClick={() =>
                        setMobileMenu((open) => (open === 'filter' ? '' : 'filter'))
                      }
                    >
                      <FilterIcon />
                      {filterActive ? (
                        <span className="messages-refine-badge" aria-hidden="true" />
                      ) : null}
                    </button>
                    <button
                      type="button"
                      className={`messages-refine-btn${
                        sortActive || mobileMenu === 'sort' ? ' is-active' : ''
                      }`}
                      aria-label={`Sort conversations: ${activeSortLabel}`}
                      title={`Sort: ${activeSortLabel}`}
                      aria-expanded={mobileMenu === 'sort'}
                      aria-haspopup="menu"
                      onClick={() =>
                        setMobileMenu((open) => (open === 'sort' ? '' : 'sort'))
                      }
                    >
                      <SortIcon />
                      {sortActive ? (
                        <span className="messages-refine-badge" aria-hidden="true" />
                      ) : null}
                    </button>
                  </div>

                  {mobileMenu === 'filter' ? (
                    <div className="messages-refine-menu" role="menu" aria-label="Filter">
                      <p className="messages-refine-menu-label">Filter</p>
                      {[...filterGroups.top, ...filterGroups.status].map((option) => (
                        <button
                          key={option.id}
                          type="button"
                          role="menuitemradio"
                          aria-checked={filterBy === option.id}
                          className={`messages-refine-option${
                            filterBy === option.id ? ' is-selected' : ''
                          }`}
                          onClick={() => onFilterChange(option.id)}
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>
                  ) : null}

                  {mobileMenu === 'sort' ? (
                    <div className="messages-refine-menu" role="menu" aria-label="Sort by">
                      <p className="messages-refine-menu-label">Sort by</p>
                      {MESSAGES_SORT_OPTIONS.map((option) => (
                        <button
                          key={option.id}
                          type="button"
                          role="menuitemradio"
                          aria-checked={sortBy === option.id}
                          className={`messages-refine-option${
                            sortBy === option.id ? ' is-selected' : ''
                          }`}
                          onClick={() => onSortChange(option.id)}
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>
                  ) : null}
                </>
              ) : (
                <>
                  <label className="field messages-search-field">
                    <span>Find a demand conversation</span>
                    <input
                      type="search"
                      value={q}
                      onChange={(e) => setQ(e.target.value)}
                      placeholder="Debtor, company, Demand ID, account, invoice…"
                    />
                  </label>
                  <label className="field messages-filter-field">
                    <span>Filter</span>
                    <select
                      value={filterBy}
                      onChange={(e) => onFilterChange(e.target.value)}
                      aria-label="Filter conversations"
                    >
                      {filterGroups.top.map((option) => (
                        <option key={option.id} value={option.id}>
                          {option.label}
                        </option>
                      ))}
                      <optgroup label="Status">
                        {filterGroups.status.map((option) => (
                          <option key={option.id} value={option.id}>
                            {option.label}
                          </option>
                        ))}
                      </optgroup>
                    </select>
                  </label>
                  <label className="field messages-sort-field">
                    <span>Sort by</span>
                    <select
                      value={sortBy}
                      onChange={(e) => onSortChange(e.target.value)}
                      aria-label="Sort conversations"
                    >
                      {MESSAGES_SORT_OPTIONS.map((option) => (
                        <option key={option.id} value={option.id}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </label>
                </>
              )}
            </div>
          ) : null}

          {filtered.length ? (
            <div className={workspaceClassName}>
              <aside className="messages-conversation-list" aria-label="Demand conversations">
                <ul>
                  {pageThreads.map((thread) => {
                    const matter = thread.matter;
                    const isActive = active && String(active.matterId) === String(thread.matterId);
                    const latestEmail = String(thread.latest?.Sender_Email || '').trim().toLowerCase();
                    const viewerEmail = String(viewer.email || '').trim().toLowerCase();
                    const fromYou = latestEmail
                      ? latestEmail === viewerEmail
                      : String(thread.latest?.Sender_Role || '').toUpperCase() === 'CLIENT';
                    const senderName = String(thread.latest?.Sender_Name || '').trim();
                    const previewPrefix = fromYou ? 'You: ' : senderName ? `${senderName}: ` : '';
                    const demandId = matterDemandId(matter);
                    const title = conversationTitle(matter, thread.matterId);
                    const account = matterAccountNumber(matter);
                    const invoice = matterInvoiceNumber(matter);
                    const unread = isConversationUnread(thread, viewer, seenMap);
                    return (
                      <li key={thread.matterId}>
                        <button
                          type="button"
                          className={[
                            'messages-conversation-item',
                            isActive ? 'is-active' : '',
                            unread ? 'is-unread' : '',
                          ]
                            .filter(Boolean)
                            .join(' ')}
                          onClick={() => selectConversation(thread.matterId)}
                        >
                          <div className="messages-inbox-top">
                            <strong className="messages-inbox-debtor">
                              {unread ? (
                                <span className="messages-unread-dot" aria-hidden="true" />
                              ) : null}
                              {title}
                              {demandId ? (
                                <span className="messages-demand-id"> · {demandId}</span>
                              ) : null}
                              {unread ? <span className="sr-only"> (unread)</span> : null}
                            </strong>
                            <time>{formatWhen(thread.updatedAt, { compact: isMobileLayout })}</time>
                          </div>
                          <div className="messages-inbox-meta">
                            <span>{attorneyLabel(matter)}</span>
                            {matter ? <span>{statusLabel(matter.Status)}</span> : null}
                            {account ? <span>Account # {account}</span> : null}
                            {invoice ? <span>Invoice # {invoice}</span> : null}
                          </div>
                          <p className="messages-inbox-preview">
                            {thread.messageCount
                              ? `${previewPrefix}${thread.preview}`
                              : 'No messages yet — start the conversation'}
                          </p>
                        </button>
                      </li>
                    );
                  })}
                </ul>
                {!mobileThreadOpen ? (
                  <ListPagination
                    className="messages-list-pagination"
                    page={pagination.page}
                    pageCount={pagination.pageCount}
                    pageSize={pagination.pageSize}
                    onPageSizeChange={pagination.setPageSize}
                    rangeStart={pagination.rangeStart}
                    rangeEnd={pagination.rangeEnd}
                    canPrev={pagination.canPrev}
                    canNext={pagination.canNext}
                    onPrev={pagination.goPrev}
                    onNext={pagination.goNext}
                  />
                ) : null}
              </aside>

              <section className="messages-thread-panel" aria-label="Selected demand conversation">
                {active?.matter ? (
                  <>
                    <header className="messages-thread-head">
                      <div className="messages-thread-head-main">
                        <button
                          type="button"
                          className="messages-mobile-back"
                          onClick={backToInbox}
                          aria-label="Back"
                          title="Back"
                        >
                          <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true">
                            <path
                              d="M15.5 5.5 9 12l6.5 6.5"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2.25"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        </button>
                        <div>
                          <h2 className="messages-thread-debtor">
                            {conversationTitle(active.matter, active.matterId)}
                            {activeDemandId ? (
                              <span className="messages-demand-id"> · {activeDemandId}</span>
                            ) : null}
                          </h2>
                          <p>
                            {attorneyLabel(active.matter)} · {statusLabel(active.matter.Status)}
                            {activeAccount ? ` · Account # ${activeAccount}` : ''}
                            {activeInvoice ? ` · Invoice # ${activeInvoice}` : ''}
                          </p>
                        </div>
                      </div>
                      <button
                        type="button"
                        className="btn-ghost messages-open-demand"
                        onClick={() => openMatter(active.matter, '/client/messages')}
                      >
                        Open demand record
                      </button>
                    </header>

                    <div className="message-thread messages-thread-scroll" ref={threadRef}>
                      {active.messages.length ? (
                        active.messages.map((msg) => (
                          <MessageBubble
                            key={msg.ROWID}
                            message={msg}
                            canDelete
                            deleting={deletingMessageId === msg.ROWID}
                            deleteDisabled={Boolean(deletingMessageId) || pending}
                            onDelete={deleteMessage}
                          />
                        ))
                      ) : (
                        <p className="empty-inline">
                          No messages on this demand yet. Send a note to your attorney
                          below.
                        </p>
                      )}
                    </div>

                    <MessageComposer
                      draft={draft}
                      onDraftChange={setDraft}
                      attachmentFiles={attachmentFiles}
                      onAttachmentFilesChange={onAttachmentFilesChange}
                      onSubmit={sendMessage}
                      pending={pending}
                      error={error}
                      placeholder={messageComposerPlaceholder(
                        active.matter,
                        isMobileLayout
                          ? 'Write a message…'
                          : 'Ask a question or share an update about this demand…'
                      )}
                    />
                  </>
                ) : (
                  <div className="messages-empty messages-thread-empty">
                    <p className="settings-sublead">
                      Select a demand conversation to message your attorney.
                    </p>
                  </div>
                )}
              </section>
            </div>
          ) : (
            <div className="messages-empty">
              <p className="settings-sublead">
                No demand conversations match this search or filter.
              </p>
            </div>
          )}
        </>
      )}
    </div>
  );
}
